/**
 * React Query hooks for Donors
 * Provides cached, optimized access to donor records
 */
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import {
  getAllDonors,
  getDonorById,
  createDonor,
  updateDonor,
  deleteDonor,
  Donor,
  DonorFilters,
} from '../services/donorService';

/**
 * Hook to fetch donors with optional search and server-side pagination
 * Automatically caches and deduplicates requests
 */
export function useDonors(filters?: DonorFilters & { enabled?: boolean }) {
  const { enabled, ...queryFilters } = filters || {};
  const normalizedFilters: Partial<DonorFilters> = Object.fromEntries(
    Object.entries(queryFilters).filter(([, value]) => value !== undefined)
  );

  return useQuery({
    queryKey: ['donors', normalizedFilters],
    queryFn: () => getAllDonors(queryFilters),
    enabled: enabled !== false,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: true, // Refetch when user returns to tab (background refetching)
    // Keep prior results on screen while the next page/search query loads,
    // matching the fix applied to useIndividuals — otherwise every keystroke
    // or page change blanks the list because the new query key has no cache.
    placeholderData: keepPreviousData,
  });
}

/**
 * Hook to fetch a single donor by ID
 */
export function useDonor(donorID: number | string | undefined) {
  return useQuery({
    queryKey: ['donor', donorID],
    queryFn: () => getDonorById(Number(donorID)),
    enabled: !!donorID,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
}

/**
 * Hook to create a new donor
 */
export function useCreateDonor() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createDonor,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['donors'] });
    },
  });
}

/**
 * Hook to update a donor
 */
export function useUpdateDonor() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ donorID, data }: { donorID: number; data: Partial<Donor> }) =>
      updateDonor(donorID, data),
    onSuccess: (data, variables) => {
      queryClient.setQueryData(['donor', variables.donorID], data);
      queryClient.invalidateQueries({ queryKey: ['donors'] });
    },
  });
}

/**
 * Hook to delete a donor
 */
export function useDeleteDonor() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteDonor,
    onSuccess: (_, deletedId) => {
      queryClient.removeQueries({ queryKey: ['donor', deletedId] });
      queryClient.invalidateQueries({ queryKey: ['donors'] });
    },
  });
}
